// src/runtime/KnowledgeFlywheel/service.ts
// ============================================
// 知识飞轮服务 - 软件操作 → 知识自动沉淀 → 反哺决策 → 优化软件
//
// 核心机制：
//   1. 监听 RuntimeManager 的 execute 事件（软件操作日志）
//   2. 使用 LLMRuntime 提取操作中的知识点
//   3. 自动写入 Obsidian 09_Knowledge（知识中心）/Cards/
//   4. 知识卡片可供 CEO Brain 决策参考
//   5. 决策结果反馈优化软件配置
//
// Token 优化：
//   - 知识提取是手动触发（按钮），不是自动监听
//   - 批量处理：累积 N 条操作后一次性提取
//   - 降级：LLM 不可用时跳过，不阻塞
// ============================================

import { RuntimeName } from '../../types/runtime';
import { LLMRole } from '../LLMRuntime/types';

// ============================================
// 类型定义
// ============================================

/** 软件操作记录 */
export interface SoftwareOperation {
  /** 操作 ID */
  id: string;
  /** Runtime 名称 */
  runtime: string;
  /** 执行的命令 */
  command: string;
  /** 操作参数（脱敏后） */
  params?: Record<string, any>;
  /** 执行结果 */
  result?: {
    success: boolean;
    data?: any;
    error?: string;
  };
  /** 操作时间 */
  timestamp: string;
  /** 操作耗时（毫秒） */
  durationMs?: number;
  /** 操作者 */
  operator?: string;
}

/** 知识沉淀请求 */
export interface KnowledgeExtractionRequest {
  /** 待提取的操作记录 */
  operations: SoftwareOperation[];
  /** 目标知识领域 */
  domain?: string;
  /** 是否写入 Vault */
  writeToVault?: boolean;
}

/** 知识沉淀结果 */
export interface KnowledgeExtractionResult {
  /** 是否成功 */
  success: boolean;
  /** 提取的知识卡片 */
  cards: ExtractedKnowledgeCard[];
  /** 写入 Vault 的路径 */
  vaultPaths?: string[];
  /** LLM 消耗 token 数 */
  tokensUsed?: number;
  /** 错误信息 */
  error?: string;
}

/** 提取的知识卡片 */
export interface ExtractedKnowledgeCard {
  /** 卡片标题 */
  title: string;
  /** 知识领域 */
  domain: string;
  /** 卡片内容（Markdown） */
  content: string;
  /** 关键洞察 */
  insights: string[];
  /** 关联的 Runtime */
  relatedRuntime: string;
  /** 建议的标签 */
  tags: string[];
}

/** 飞轮状态 */
export interface FlywheelState {
  /** 累计操作记录数 */
  totalOperations: number;
  /** 已提取知识卡片数 */
  totalCards: number;
  /** 待处理操作数 */
  pendingOperations: number;
  /** 最近一次提取时间 */
  lastExtractionAt?: string;
  /** 飞轮闭环度（0-100） */
  闭环度: number;
  /** 已反哺到 Hermes 配置的次数（飞轮第 5 步：决策反馈优化软件配置） */
  feedbackAppliedCount: number;
}

// ============================================
// 知识飞轮服务
// ============================================

export class KnowledgeFlywheelService {
  private logger: Console;
  private runtimeManager: any = null;
  private operationBuffer: SoftwareOperation[] = [];
  private readonly BUFFER_THRESHOLD = 10;
  private state: FlywheelState = {
    totalOperations: 0,
    totalCards: 0,
    pendingOperations: 0,
    闭环度: 0,
    feedbackAppliedCount: 0,
  };

  constructor(runtimeManager?: any, logger: Console = console) {
    this.logger = logger;
    this.runtimeManager = runtimeManager;
  }

  /** 注入 RuntimeManager */
  setRuntimeManager(rm: any): void {
    this.runtimeManager = rm;
  }

  /**
   * 记录软件操作（不消耗 token，仅缓存）
   *
   * 由 RuntimeManager.execute 包装层调用
   */
  recordOperation(operation: SoftwareOperation): void {
    this.operationBuffer.push(operation);
    this.state.totalOperations++;
    this.state.pendingOperations = this.operationBuffer.length;
    this.logger.info(
      `[KnowledgeFlywheel] 操作已记录: ${operation.runtime}/${operation.command} (累计: ${this.state.totalOperations}, 待处理: ${this.state.pendingOperations})`
    );
  }

  /**
   * 手动触发知识提取（消耗 token）
   *
   * Token 优化：仅在用户点击"提取知识"按钮时触发
   */
  async extractKnowledge(
    request?: KnowledgeExtractionRequest
  ): Promise<KnowledgeExtractionResult> {
    const operations = request?.operations || this.operationBuffer.splice(0, this.BUFFER_THRESHOLD);
    if (operations.length === 0) {
      return { success: true, cards: [] };
    }

    this.logger.info(`[KnowledgeFlywheel] 开始提取知识，待处理操作: ${operations.length}`);

    try {
      // 检查 LLMRuntime 是否可用
      if (!this.runtimeManager || !this.runtimeManager.hasRuntime(RuntimeName.LLM)) {
        this.logger.warn('[KnowledgeFlywheel] LLMRuntime 未注册，跳过知识提取');
        return { success: false, cards: [], error: 'LLMRuntime 未注册' };
      }

      // 构造提取 prompt
      const operationsDigest = operations.map((op) => ({
        runtime: op.runtime,
        command: op.command,
        success: op.result?.success,
        durationMs: op.durationMs,
        timestamp: op.timestamp,
      }));

      const systemPrompt = `你是 XEAOS 知识飞轮引擎。请从用户的软件操作记录中提取可复用的知识卡片。

要求：
1. 分析操作模式、成功/失败原因、最佳实践
2. 提取可复用的方法论、配置经验、避坑指南
3. 返回严格的 JSON 数组，每个对象包含：
   - title: 知识卡片标题（20字以内）
   - domain: 知识领域（如 Agent/CRM/Quant/Marketing 等）
   - content: Markdown 格式的知识内容（200-400字）
   - insights: 关键洞察数组（3-5条）
   - relatedRuntime: 关联的 Runtime 名称
   - tags: 建议标签数组
4. 如果操作记录无价值，返回空数组 []`;

      const userPrompt = `以下是 ${operations.length} 条软件操作记录：

${JSON.stringify(operationsDigest, null, 2)}

请提取知识卡片，返回 JSON 数组。`;

      // 调用 LLMRuntime（Writer 角色）
      const response = await this.runtimeManager.execute(
        RuntimeName.LLM,
        'chat',
        {
          role: LLMRole.Writer,
          req: {
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: userPrompt },
            ],
            temperature: 0.5,
            maxTokens: 3000,
          },
        }
      );

      if (!response.success) {
        return { success: false, cards: [], error: response.error || 'LLM 调用失败' };
      }

      const llmResponse = response.data as { content?: string; tokensUsed?: number } | undefined;
      const content = llmResponse?.content || '';
      if (!content) {
        return { success: false, cards: [], error: 'LLM 返回空内容' };
      }

      // 解析 JSON
      const cards = this.parseKnowledgeCards(content, operations);

      // 写入 Vault
      let vaultPaths: string[] = [];
      if (request?.writeToVault !== false && cards.length > 0) {
        vaultPaths = await this.writeCardsToVault(cards);
      }

      // 更新状态
      this.state.totalCards += cards.length;
      this.state.pendingOperations = this.operationBuffer.length;
      this.state.lastExtractionAt = new Date().toISOString();
      this.updateFlywheelClosure();

      this.logger.info(
        `[KnowledgeFlywheel] 知识提取完成: ${cards.length} 张卡片, 消耗 ${llmResponse?.tokensUsed || 0} tokens`
      );

      return {
        success: true,
        cards,
        vaultPaths,
        tokensUsed: llmResponse?.tokensUsed,
      };
    } catch (err: any) {
      this.logger.error('[KnowledgeFlywheel] 知识提取失败:', err);
      return { success: false, cards: [], error: err?.message || String(err) };
    }
  }

  /**
   * 解析 LLM 返回的知识卡片
   */
  private parseKnowledgeCards(content: string, operations: SoftwareOperation[]): ExtractedKnowledgeCard[] {
    try {
      // 兼容 markdown 代码块
      const jsonStr = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(jsonStr);
      if (!Array.isArray(parsed)) return [];

      return parsed.map((card: any) => ({
        title: card.title || '未命名知识',
        domain: card.domain || 'General',
        content: card.content || '',
        insights: Array.isArray(card.insights) ? card.insights : [],
        relatedRuntime: card.relatedRuntime || operations[0]?.runtime || 'unknown',
        tags: Array.isArray(card.tags) ? card.tags : ['知识飞轮'],
      }));
    } catch {
      this.logger.warn('[KnowledgeFlywheel] LLM 返回内容无法解析为 JSON');
      return [];
    }
  }

  /**
   * 写入知识卡片到 Obsidian Vault
   */
  private async writeCardsToVault(cards: ExtractedKnowledgeCard[]): Promise<string[]> {
    const paths: string[] = [];
    const date = new Date().toISOString().slice(0, 10);

    for (const card of cards) {
      const vaultPath = `09_Knowledge（知识中心）/Cards/${card.domain}/飞轮-${card.title}-${date}.md`;
      const frontmatter = [
        '---',
        `title: ${card.title}`,
        `created: ${date}`,
        `updated: ${date}`,
        'type: 知识卡片',
        `domain: ${card.domain}`,
        `title: ${card.title}`,
        `summary: ${card.content.slice(0, 100)}...`,
        'status: 已发布',
        'priority: P2',
        `impact: 5`,
        `created: ${date}`,
        `updated: ${date}`,
        'version: v1.0',
        'author: KnowledgeFlywheel',
        'owner: Hermes',
        'runtime: Knowledge',
        'rating: 3',
        'verified: false',
        'tags:',
        `  - 知识/Cards`,
        `  - 知识/${card.domain}`,
        '  - 知识飞轮',
        ...card.tags.map((t) => `  - ${t}`),
        `related_runtime: ${card.relatedRuntime}`,
        `source: KnowledgeFlywheel`,
        '---',
        '',
        `# ${card.title}`,
        '',
        card.content,
        '',
        '## 关键洞察',
        '',
        ...card.insights.map((i) => `- ${i}`),
        '',
        '## 关联 Runtime',
        '',
        `- ${card.relatedRuntime}`,
        '',
        '## 标签',
        '',
        card.tags.map((t) => `#${t}`).join(' '),
        '',
      ].join('\n');

      try {
        // 通过 Vault API 写入
        await fetch(`/vault-api/write?path=${encodeURIComponent(vaultPath)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: frontmatter }),
        });
        paths.push(vaultPath);
      } catch (err) {
        this.logger.warn(`[KnowledgeFlywheel] 写入 Vault 失败: ${vaultPath}`, err);
      }
    }

    return paths;
  }

  /**
   * 更新飞轮闭环度
   *
   * 闭环度公式：(已提取卡片 + 反馈应用次数) / (累计操作 * 2) * 100
   * 分母乘 2 的原因：飞轮 5 步机制中，操作→卡片→反馈 共 2 个转化环节，
   * 每个环节独立计入闭环度，反馈环节也纳入闭环度计算。
   */
  private updateFlywheelClosure(): void {
    const totalOps = this.state.totalOperations || 0;
    if (totalOps === 0) {
      this.state.闭环度 = 0;
      return;
    }
    const numerator = this.state.totalCards + this.state.feedbackAppliedCount;
    const percent = (numerator / (totalOps * 2)) * 100;
    this.state.闭环度 = Math.min(100, Math.round(percent));
  }

  /**
   * 记录反馈应用次数（飞轮第 5 步：决策反馈优化软件配置）
   *
   * 由外部组件在成功调用 Hermes.optimize_config 后调用，
   * 用于将反馈环节纳入闭环度计算。
   */
  recordFeedbackApplied(count: number = 1): void {
    if (count <= 0) return;
    this.state.feedbackAppliedCount += count;
    this.updateFlywheelClosure();
    this.logger.info(
      `[KnowledgeFlywheel] 反馈已应用: +${count} (累计反馈: ${this.state.feedbackAppliedCount})`
    );
  }

  /**
   * 获取飞轮状态
   */
  getState(): FlywheelState {
    return { ...this.state };
  }

  /**
   * 获取待处理操作列表
   */
  getPendingOperations(): SoftwareOperation[] {
    return [...this.operationBuffer];
  }

  /**
   * 清空操作缓冲区
   */
  clearBuffer(): void {
    this.operationBuffer = [];
    this.state.pendingOperations = 0;
  }
}

// ============================================
// 单例
// ============================================

let flywheelInstance: KnowledgeFlywheelService | null = null;

export function getKnowledgeFlywheelService(runtimeManager?: any): KnowledgeFlywheelService {
  if (!flywheelInstance) {
    flywheelInstance = new KnowledgeFlywheelService(runtimeManager);
  } else if (runtimeManager && !flywheelInstance['runtimeManager']) {
    flywheelInstance.setRuntimeManager(runtimeManager);
  }
  return flywheelInstance;
}
